'use client';
import React, { useState, useMemo } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { Icon } from './Icon';
import { HealthDot } from './HealthDot';
import { useHealth } from '@/lib/health';
import { SHOW_PORTAL_HOME } from '@/lib/flags';
import { apiClient } from '@/lib/apiClient';
import { FE_ONLY } from '@/lib/flags';
import { SafeLink } from './SafeLink';

type Item = { href: string; label: string; icon: any; health?: string; subText?: string; external?: boolean; archived?: boolean };
type Section = { title: string; items: Item[] };

interface DocItem {
  id: string;
  title: string;
}

interface Module {
  name: string;
  title: string;
  description: string;
  doc_count: number;
}

interface DocInfo {
  filename: string;
  title: string;
  path: string;
  full_path: string;
  is_core: boolean;
}

interface ModuleStructure {
  core_docs: DocInfo[];
  directories: Record<string, DocInfo[]>;
  all_docs: DocInfo[];
}

interface Role {
  id: string;
  name: string;
  title: string;
  description: string;
  icon: string;
  color: string;
  doc_count: number;
}

const coreModules: Item[] = [
  { href: '/helper', label: 'Helper System', icon: 'home', health: '/api/helper/health' },
  { href: '/brain/', label: 'Second Brain', icon: 'book', health: '/api/vexa/health', external: true },
  { href: '/rag', label: 'RAG System', icon: 'bot', health: '/api/rag/health' },
  { href: '/eval', label: 'Clay', icon: 'dashboard', health: '/claya/health' },
  { href: 'https://basbott.tinytinytelg2.space', label: 'BasBot LLM agent', icon: 'login', health: '/api/status', external: true },
  { href: 'https://avideo.tinytinytelg2.space', label: 'avideo', icon: 'file', health: '/api/avideo/health', external: true },
  { href: '/compliance-matrix/', label: 'RCS → SMS fallback', icon: 'check', health: '/api/rca-methodology/prompt-template', external: true },
];

const moduleContextSections: Record<string, Section> = {
  '/rag': {
    title: 'RAG SYSTEM',
    items: [
      { href: '/rag', label: '➕ New Chat', icon: '' },
    ]
  },
  '/mark': {
    title: 'MARK SYSTEM',
    items: [
      { href: '/mark', label: '📊 Article Dashboard', icon: '' },
      { href: '/mark/submit', label: '➕ Submit Article', icon: '' },
    ]
  },
  '/helper': {
    title: 'HELPER SYSTEM',
    items: [
      { href: '/helper', label: '🎫', icon: 'dashboard' },
      { href: '/helper/candidates-for-closing', label: '✅', icon: 'check' },
    ]
  },
  '/docs-v3': {
    title: 'DOCUMENTATION',
    items: [
      { href: '/docs-v3', label: 'Documentation Hub', icon: '' },
      { href: '/docs-v3/architecture', label: '🏗️ Architecture', icon: '' },
      { href: '/docs-v3/api', label: '📡 API Reference', icon: '' },
      { href: '/docs-v3/development', label: '💻 Development', icon: '' },
      { href: '/docs-v3/operations', label: '⚙️ Operations', icon: '' },
      { href: '/docs-v3/security', label: '🔒 Security', icon: '' },
      { href: '/docs-v3/analysis-results', label: '📊 Analysis Results', icon: '' },
    ]
  },
};

export function Sidebar({ compact }: { compact: boolean }) {
  const pathname = usePathname();
  const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>({});
  const [mounted, setMounted] = useState(false);
  const [docItems, setDocItems] = useState<DocItem[]>([]);
  const [localModules, setLocalModules] = useState<Module[]>([]);
  const [localModuleStructures, setLocalModuleStructures] = useState<Record<string, ModuleStructure>>({});
  const [coreModulesCollapsed, setCoreModulesCollapsed] = useState(false);
  
  // Don't use docs context in main sidebar - it's not wrapped in DocsProvider
  // The docs pages have their own DocsSidebar component that uses the context
  const contextModules: Module[] = [];
  const contextModuleStructures: Record<string, ModuleStructure> = {};
  
  // Always ensure modules is an array
  const modules = Array.isArray(contextModules) && contextModules.length > 0 ? contextModules : 
                  Array.isArray(localModules) ? localModules : [];
  const moduleStructures = Object.keys(contextModuleStructures).length > 0 ? contextModuleStructures : localModuleStructures;
  
  // Debug logging
  if (pathname.startsWith('/docs')) {
    console.log('Sidebar debug:', {
      pathname,
      hasDocsContext: false, // Main sidebar doesn't use docs context
      contextModulesLength: contextModules.length,
      localModulesLength: localModules.length,
      finalModulesLength: modules.length,
      modulesIsArray: Array.isArray(modules)
    });
  }
  
  const ragHealth = useHealth('/api/rag/health');
  const helperHealth = useHealth('/api/helper/health');
  const clayHealth = useHealth('/claya/health');
  const brainHealth = useHealth('/api/vexa/health');
  const avideoHealth = useHealth('/api/avideo/health');
  const basbotHealth = useHealth('/api/status');
  const rcsHealth = useHealth('/api/rca-methodology/prompt-template');
  const healthByEndpoint: Record<string, 'up' | 'down' | 'unknown'> = {
    '/api/rag/health': ragHealth,
    '/api/helper/health': helperHealth,
    '/claya/health': clayHealth,
    '/api/vexa/health': brainHealth,
    '/api/avideo/health': avideoHealth,
    '/api/status': basbotHealth,
    '/api/rca-methodology/prompt-template': rcsHealth,
  };
  
  // Avoid hydration mismatch
  React.useEffect(() => {
    setMounted(true);
  }, []);

  // Load documentation modules and structures
  React.useEffect(() => {
    if (FE_ONLY) return;
    
    // Clear state when switching to docs-v2 or other pages
    if (!pathname.startsWith('/docs') || pathname.startsWith('/docs-v2')) {
      setLocalModules([]);
      setLocalModuleStructures({});
      return;
    }
    
    let alive = true;
    (async () => {
      try {
        const req = apiClient('docs');
        // Load modules
        const modulesData = await req<{modules: Module[]}>('/api/modules');
        if (alive && modulesData?.modules) {
          setLocalModules(modulesData.modules);
          
          // Load structure for each module
          const structures: Record<string, ModuleStructure> = {};
          for (const module of modulesData.modules) {
            try {
              const moduleData = await req<{module: Module, structure: ModuleStructure}>(`/api/modules/${module.name}`);
              if (alive && moduleData?.structure) {
                structures[module.name] = moduleData.structure;
              }
            } catch (e) {
              // Silently fail for individual modules
            }
          }
          if (alive) {
            setLocalModuleStructures(structures);
          }
        }
      } catch (e) {
        // Silently fail - docs may not be available
      }
    })();
    return () => { alive = false };
  }, [pathname]);


  // Determine current module based on pathname
  const currentModule = useMemo(() => {
    console.log('currentModule detection:', { pathname });
    // Sort by length descending to match more specific paths first
    const sortedModules = [...coreModules].sort((a, b) => b.href.length - a.href.length);
    for (const module of sortedModules) {
      if (pathname.startsWith(module.href)) {
        console.log('currentModule matched:', module.href);
        return module.href;
      }
    }
    console.log('currentModule: no match');
    return null;
  }, [pathname]);

  // Helper functions
  const getDocIcon = (filename: string): string => {
    if (filename.startsWith('L1-')) return '💼';
    if (filename.startsWith('L2-')) return '🏗️';
    if (filename.startsWith('L3-')) return '🔧';
    if (filename.startsWith('L4-')) return '🔗';
    if (filename.startsWith('L5-')) return '📊';
    if (filename.includes('overview')) return '📖';
    if (filename.includes('security')) return '🔒';
    if (filename.includes('api')) return '🔌';
    if (filename.includes('data')) return '💾';
    if (filename.includes('ops') || filename.includes('runbook')) return '⚙️';
    if (filename.includes('integration')) return '🔄';
    return '📄';
  };

  const getDirectoryIcon = (dirName: string): string => {
    if (dirName === 'apis') return '🔌';
    if (dirName === 'integrations') return '🔄';
    if (dirName === 'ops') return '⚙️';
    if (dirName === 'runbooks') return '📋';
    if (dirName === 'security') return '🔒';
    return '📁';
  };

  // Determine current documentation module from path
  const currentDocModule = useMemo(() => {
    if (!pathname.startsWith('/docs/')) return null;
    const pathParts = pathname.split('/');
    return pathParts[2] || null; // /docs/[module]/...
  }, [pathname]);

  // Generate context section for current module
  const contextSection = useMemo((): Section | null => {
    console.log('contextSection memo:', { currentModule, pathname });
    if (!currentModule) return null;
    
    const section = moduleContextSections[currentModule];
    if (!section) return null;
    
    // For docs, show hierarchical module structure
    if (currentModule === '/docs') {
      // STOP THE BLEED: Return skeleton section if modules not ready
      console.log('Sidebar contextSection check:', {
        modulesType: typeof modules,
        modulesIsArray: Array.isArray(modules),
        modulesLength: modules?.length,
        modules
      });
      
      if (!Array.isArray(modules) || modules.length === 0) {
        console.log('Sidebar returning skeleton');
        return {
          title: 'DOCUMENTATION',
          items: [
            { href: '/docs', label: 'Documentation Hub', icon: '' }
            // Don't include loading item as a link - it will be handled as a non-clickable item
          ]
        };
      }
      
      console.log('Sidebar proceeding with modules:', modules.length);
      
      const items: Item[] = [];

      // Sort modules to ensure consistent order
      const sortedModules = [...modules].sort((a, b) => {
        // Billing comes first, then kubernetes, then alphabetical
        if (a.name === 'billing') return -1;
        if (b.name === 'billing') return 1;
        if (a.name === 'kubernetes') return -1;
        if (b.name === 'kubernetes') return 1;
        return a.name.localeCompare(b.name);
      });

      // Add modules
      sortedModules.forEach(module => {
        const structure = moduleStructures[module.name];
        const isCurrentModule = currentDocModule === module.name;
        const isCollapsed = collapsedSections[`module-${module.name}`] && !isCurrentModule;

        // Module header with special styling
        items.push({
          href: `/docs/${module.name}`,
          label: module.title,
          icon: module.name === 'billing' ? '💰' : module.name === 'kubernetes' ? '☸️' : '📁',
          subText: `module` // Mark as module for styling
        });

        // Show module contents if expanded or is current module
        if (!isCollapsed && structure) {
          // Sort core docs to ensure L1-L5 order
          const sortedCoreDocs = structure.core_docs ? [...structure.core_docs].sort((a, b) => {
            const aMatch = a?.filename?.match(/^L(\d)-/);
            const bMatch = b?.filename?.match(/^L(\d)-/);
            if (aMatch && bMatch) {
              return parseInt(aMatch[1]) - parseInt(bMatch[1]);
            }
            if (aMatch) return -1;
            if (bMatch) return 1;
            // Overview comes after L1-L5
            if (a?.filename?.includes('overview')) return -1;
            if (b?.filename?.includes('overview')) return 1;
            return (a?.filename || '').localeCompare(b?.filename || '');
          }) : [];
          
          // Core docs (L1-L5 first, then overview)
          sortedCoreDocs.forEach(doc => {
            if (!doc) return;
            items.push({
              href: `/docs/${module.name}/${(doc.path || '').replace('.md', '')}`,
              label: doc.title || 'Untitled',
              icon: getDocIcon(doc?.filename || ''),
              subText: 'doc' // Mark as doc for styling
            });
          });

          // Directories (but limit to avoid clutter)
          const sortedDirs = (structure.directories && typeof structure.directories === 'object') ? Object.entries(structure.directories).sort(([a], [b]) => {
            // APIs first, then integrations, then others
            const order = ['apis', 'integrations', 'ops', 'runbooks', 'security'];
            const aIndex = order.indexOf(a);
            const bIndex = order.indexOf(b);
            if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
            if (aIndex !== -1) return -1;
            if (bIndex !== -1) return 1;
            return a.localeCompare(b);
          }) : [];
          
          sortedDirs.slice(0, 5).forEach(([dirName, docs]) => {
            if (!docs || !Array.isArray(docs)) return;
            // Add directory header - use empty href for non-clickable header
            items.push({
              href: '', // Empty href will be handled by SafeLink as non-clickable
              label: dirName.replace(/-/g, ' ').toUpperCase(),
              icon: getDirectoryIcon(dirName),
              subText: `dir-${docs.length}` // Mark as directory with count
            });

            // Add top few docs from this directory if it's the current module
            if (isCurrentModule && docs && docs.length > 0) {
              docs.slice(0, 3).forEach(doc => {
                if (!doc) return;
                items.push({
                  href: `/docs/${module.name}/${(doc.path || '').replace('.md', '')}`,
                  label: doc.title || 'Untitled',
                  icon: getDocIcon(doc.filename || ''),
                  subText: 'subdoc' // Mark as subdoc for styling
                });
              });
            }
          });
        }
      });

      return {
        title: section.title,
        items
      };
    }
    
    return section;
  }, [currentModule, modules, moduleStructures, currentDocModule, collapsedSections]);
  
  const toggleSection = React.useCallback((title: string) => {
    setCollapsedSections(prev => ({
      ...prev,
      [title]: !prev[title]
    }));
  }, []);
  
  if (!mounted) {
    return (
      <aside className={'h-full bg-white border-r border-[#e5e1d6] overflow-y-auto relative'}
             style={{ width: compact ? 'var(--sidebar-compact-w)' : 'var(--sidebar-w)' }}>
        <div className="h-full px-3 py-4">
          <div className="text-sm text-[#6b7280]">Loading...</div>
        </div>
      </aside>
    );
  }

  return (
    <aside className={'h-full bg-white border-r border-[#e5e1d6] relative'}
           style={{ width: compact ? 'var(--sidebar-compact-w)' : 'var(--sidebar-w)' }}>
      <div className="h-full px-3 py-4 overflow-y-auto overflow-x-hidden">
        {/* Core Modules Section */}
        <div className="mb-6">
          <div className={`px-2 text-[11px] font-semibold tracking-wider text-gray-500 flex items-center justify-between ${compact ? 'text-center' : ''}`}>
            <span>{SHOW_PORTAL_HOME ? 'CORE MODULES' : 'PROJECTS'}</span>
            {!compact && (
              <button
                onClick={() => setCoreModulesCollapsed(!coreModulesCollapsed)}
                className="text-xs text-gray-400 hover:text-gray-600 transition-colors"
                aria-label={coreModulesCollapsed ? 'Expand core modules' : 'Collapse core modules'}
              >
                {coreModulesCollapsed ? '▶' : '▼'}
              </button>
            )}
          </div>
          {!coreModulesCollapsed && (
            <nav className="mt-2 space-y-1">
              {/* Portal Home if enabled */}
              {SHOW_PORTAL_HOME && (
                <Link href="/portal" className={
                  'group flex items-center gap-2 rounded-md px-2 py-2 ' +
                  (pathname === '/portal' ? 'bg-white text-[#1f2937] border-l-2 border-[#111827] shadow-sm' : 'text-[#1f2937] hover:bg-[#f3f1ec]')
                }>
                  <Icon name="home" className="w-4 h-4 text-[#6b7280]" />
                  {!compact && <span className="flex-1">Portal Home</span>}
                </Link>
              )}
              
              {/* Core modules */}
              {coreModules.map((module) => {
              const active = pathname === module.href || (module.href !== '/' && pathname.startsWith(module.href + '/'));
              const healthState = module.health ? (healthByEndpoint[module.health] ?? 'unknown') : 'unknown';
              
              const baseClasses = 'group flex items-center gap-2 rounded-md px-2 py-2 ';
              
              // Normal styling for all modules
              const styleClasses = active
                ? 'bg-white text-[#1f2937] border-l-2 border-[#111827] shadow-sm'
                : 'text-[#1f2937] hover:bg-[#f3f1ec]';

              if (module.external) {
                const newTab = module.href.startsWith('http');
                return (
                  <a
                    key={module.href}
                    href={module.href}
                    target={newTab ? '_blank' : undefined}
                    rel={newTab ? 'noreferrer' : undefined}
                    className={baseClasses + styleClasses}
                  >
                    <Icon name={module.icon} className="w-4 h-4 text-[#6b7280]" />
                    {!compact && <span className="flex-1">{module.label}</span>}
                    {module.health && <HealthDot state={healthState} />}
                  </a>
                );
              }
              return (
                <Link key={module.href} href={module.href} className={baseClasses + styleClasses}>
                  <Icon name={module.icon} className="w-4 h-4 text-[#6b7280]" />
                  {!compact && <span className="flex-1">{module.label}</span>}
                  {module.health && <HealthDot state={healthState} />}
                </Link>
              );
            })}
            </nav>
          )}
        </div>

        {/* Context Section for current module */}
        {contextSection && !compact && (
          <div className="mb-6">
            <div className="px-2 text-[11px] font-semibold tracking-wider text-gray-500">
              {contextSection.title}
            </div>
            <nav className="mt-2 space-y-1">
              {contextSection.items.map((item, index) => {
                const isHub = item.href === '/docs' || item.href === '/docs-v2' || item.href === '/docs-v3';
                const active = pathname === item.href || (pathname.startsWith(item.href + '/') && !item.href.includes('#'));
                const isModule = item.subText === 'module' || item.subText === 'module-active';
                const isModuleActive = item.subText === 'module-active';
                const isDirectory = item.subText?.startsWith('dir-');
                const isDoc = item.subText === 'doc';
                const isSubDoc = item.subText === 'subdoc';
                const dirCount = isDirectory && item.subText ? item.subText.replace('dir-', '') : null;
                
                // AutoDocs V2 specific checks
                const isRoleHeader = item.subText === 'role-header';
                const isCoreHeader = item.subText === 'core-header';
                const isRole = item.subText === 'role' || item.subText === 'role-active';
                const isRoleActive = item.subText === 'role-active';
                const isCore = item.subText === 'core' || item.subText === 'core-active';
                const isCoreActive = item.subText === 'core-active';
                
                // Determine styling based on type
                let className = 'group flex items-center gap-2 rounded-md transition-colors ';
                if (isRoleHeader || isCoreHeader) {
                  className += 'text-[#6b7280] font-medium text-xs uppercase tracking-wider mt-4 mb-1 px-2 py-1 pointer-events-none';
                } else if (isRole) {
                  className += isRoleActive ? 'bg-white text-[#1f2937] px-2 py-2 font-semibold mt-3 border-l-2 border-[#111827] shadow-sm' : 'text-[#1f2937] hover:bg-[#f3f1ec] px-2 py-2 font-semibold mt-3 bg-[#f3f1ec]';
                } else if (isCore) {
                  className += isCoreActive ? 'bg-white text-[#1f2937] px-2 py-2 font-medium border-l-2 border-[#111827] shadow-sm' : 'text-[#1f2937] hover:bg-[#f3f1ec] px-2 py-2';
                } else if (isHub) {
                  className += active ? 'bg-white text-[#1f2937] px-2 py-2 font-medium border-l-2 border-[#111827] shadow-sm' : 'text-[#1f2937] hover:bg-[#f3f1ec] px-2 py-2';
                } else if (isModule) {
                  className += (active || isModuleActive) ? 'bg-white text-[#1f2937] px-2 py-2 font-semibold mt-3 border-l-2 border-[#111827] shadow-sm' : 'text-[#1f2937] hover:bg-[#f3f1ec] px-2 py-2 font-semibold mt-3 bg-[#f3f1ec]';
                } else if (isDirectory) {
                  className += 'text-[#6b7280] hover:bg-[#f3f1ec] px-2 py-1 font-medium text-xs uppercase tracking-wider mt-2 ml-2';
                } else if (isDoc) {
                  className += active ? 'bg-white text-[#1f2937] px-2 py-1.5 ml-4 border-l-2 border-[#111827] shadow-sm' : 'text-[#4b5563] hover:bg-[#f3f1ec] px-2 py-1.5 ml-4';
                } else if (isSubDoc) {
                  className += active ? 'bg-white text-[#1f2937] px-2 py-1 ml-6 text-sm border-l-2 border-[#111827] shadow-sm' : 'text-[#6b7280] hover:bg-[#f3f1ec] px-2 py-1 ml-6 text-sm';
                } else {
                  className += active ? 'bg-white text-[#1f2937] px-2 py-2 border-l-2 border-[#111827] shadow-sm' : 'text-[#1f2937] hover:bg-[#f3f1ec] px-2 py-2';
                }
                
                return (
                  <SafeLink key={`${item.href}-${index}`} href={item.href} className={className}>
                    {typeof item.icon === 'string' ? (
                      <span className={`${isModule || isRole ? 'text-lg' : isDirectory ? 'text-xs' : isSubDoc ? 'text-xs' : 'text-sm'}`}>
                        {item.icon}
                      </span>
                    ) : (
                      <Icon name={item.icon} className={`${isModule || isRole ? 'w-5 h-5' : 'w-4 h-4'} text-gray-500`} />
                    )}
                    <span className={`flex-1 truncate ${isSubDoc ? 'text-xs' : isDoc ? 'text-sm' : ''}`}>
                      {item.label}
                    </span>
                    {dirCount && (
                      <span className="text-xs text-gray-400 bg-gray-100 px-1 py-0.5 rounded">
                        {dirCount}
                      </span>
                    )}
                  </SafeLink>
                );
              })}
            </nav>
          </div>
        )}
      </div>
    </aside>
  );
}
